import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ScheduledSlot } from "@/api/online-ordering-service/model";
import { formatDayKey, groupSlotsByDay, slotRange } from "@/lib/slots";

export type SlotPickerProps = {
  slots: ScheduledSlot[];
  loading: boolean;
  /** slotKey of the chosen slot, or null. */
  value: string | null;
  onChange: (slot: ScheduledSlot) => void;
};

function chip(on: boolean): React.CSSProperties {
  return {
    padding: "8px 12px",
    borderRadius: 999,
    border: `1px solid ${on ? "var(--primary-600)" : "var(--border)"}`,
    background: on ? "var(--primary-50)" : "var(--surface-1)",
    color: on ? "var(--primary-700)" : "var(--ink-1)",
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: on ? 700 : 500,
    whiteSpace: "nowrap",
    cursor: "pointer",
  };
}

const hint: React.CSSProperties = { fontSize: 13, color: "var(--ink-3)", padding: "4px 0" };

/** Server-generated scheduled slots: pick a day, then a 15-minute slot. Full slots never appear. */
export function SlotPicker({ slots, loading, value, onChange }: SlotPickerProps) {
  const { t, i18n } = useTranslation();
  const days = useMemo(() => groupSlotsByDay(slots), [slots]);
  const [dayKey, setDayKey] = useState<string | null>(null);

  useEffect(() => {
    const valueDay = value ? value.slice(0, 10) : null;
    if (valueDay && days.some((d) => d.key === valueDay)) {
      setDayKey(valueDay);
    } else if (!dayKey || !days.some((d) => d.key === dayKey)) {
      setDayKey(days[0]?.key ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, value]);

  if (loading) return <div style={hint}>{t("v2.checkout.slots_loading", "Loading times")}</div>;
  if (days.length === 0) {
    return <div style={hint}>{t("v2.checkout.slots_empty", "No times available in the next 7 days")}</div>;
  }
  const day = days.find((d) => d.key === dayKey) ?? days[0];

  return (
    <div data-testid="slot-picker">
      <div role="tablist" style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 6 }}>
        {days.map((d) => (
          <button
            key={d.key}
            type="button"
            role="tab"
            aria-selected={d.key === day.key}
            onClick={() => setDayKey(d.key)}
            style={chip(d.key === day.key)}
          >
            {formatDayKey(d.key, i18n.language)}
          </button>
        ))}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
          gap: 6,
          marginTop: 8,
        }}
      >
        {day.slots.map((s) => {
          const r = slotRange(s);
          return (
            <button
              key={s.slotKey}
              type="button"
              aria-pressed={s.slotKey === value}
              onClick={() => onChange(s)}
              style={chip(s.slotKey === value)}
            >
              {t("v2.checkout.slot_range", { from: r.from, to: r.to, defaultValue: `${r.from} to ${r.to}` })}
            </button>
          );
        })}
      </div>
    </div>
  );
}
